// Exact producer grammar verified in server/agentChat.ts (SESSION_PATTERN).
// This is a display classification only; it grants no session access.
const dashboardSessionPattern =
  /^cdash2--([0-9a-f]{32})--[a-z0-9]{1,12}--[0-9a-f]{24}$/;
export const usageFields = [
  "requestCount",
  "inputTokens",
  "outputTokens",
  "cachedTokens",
  "totalTokens",
  "actualCostUsd",
];
export const sumNullable = (a, b) => (a == null || b == null ? null : a + b);
export const money = (value) =>
  value == null
    ? "—"
    : value !== 0 && Math.abs(value) < 0.000001
      ? `${value < 0 ? "−" : ""}<$0.000001`
      : new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits:
            value !== 0 && Math.abs(value) < 0.01
              ? 6
              : Math.abs(value) < 1
                ? 4
                : 2,
        }).format(value);
export const number = (value) =>
  value == null ? "—" : new Intl.NumberFormat().format(value);
export const dateLabel = (value) =>
  value && !Number.isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleString()
    : "Time not recorded";
export function mergeSessions(providerPages, historyPages) {
  const rows = new Map();
  for (const page of providerPages)
    for (const item of page.sessions) {
      const previous = rows.get(item.id);
      rows.set(
        item.id,
        previous
          ? {
              ...previous,
              knownActualCostUsd:
                (previous.knownActualCostUsd ?? 0) + (item.actualCostUsd ?? 0),
              knownCostChunks:
                previous.knownCostChunks + (item.actualCostUsd == null ? 0 : 1),
              ...Object.fromEntries(
                usageFields.map((key) => [
                  key,
                  sumNullable(previous[key], item[key]),
                ]),
              ),
            }
          : {
              ...item,
              source: dashboardSessionPattern.test(item.id)
                ? "Dashboard"
                : "Agent/API",
              title: null,
              knownActualCostUsd: item.actualCostUsd,
              knownCostChunks: item.actualCostUsd == null ? 0 : 1,
            },
      );
    }
  for (const page of historyPages)
    for (const item of page.sessions) {
      const previous = rows.get(item.id);
      // Metadata must never overwrite provider charges; first inventory occurrence is newest.
      if (previous?.hasMetadata) continue;
      rows.set(item.id, {
        ...item,
        knownActualCostUsd: previous?.knownActualCostUsd ?? null,
        knownCostChunks: previous?.knownCostChunks ?? 0,
        ...(previous
          ? Object.fromEntries(usageFields.map((key) => [key, previous[key]]))
          : {}),
        hasMetadata: true,
      });
    }
  return [...rows.values()].sort(
    (a, b) =>
      (b.updatedAt || b.startedAt || "").localeCompare(
        a.updatedAt || a.startedAt || "",
      ) || a.id.localeCompare(b.id),
  );
}
export function aggregateUsage(pages) {
  const records = new Map();
  let inconsistent = false;
  const comparisons = new Map();
  for (const page of pages) {
    // Cross-page repeated generations make the page-scoped comparison sums unsafe.
    for (const record of page.records) {
      if (records.has(record.id)) inconsistent = true;
      else records.set(record.id, record);
    }
    for (const item of page.comparisons) {
      const previous = comparisons.get(item.id);
      if (!previous) comparisons.set(item.id, { ...item });
      else {
        const combined = {
          ...previous,
          pricedAt:
            previous.pricedAt === item.pricedAt ? previous.pricedAt : null,
        };
        for (const key of ["estimatedCostUsd", "coveredActualCostUsd"])
          combined[key] =
            previous[key] == null && item[key] == null
              ? null
              : (previous[key] ?? 0) + (item[key] ?? 0);
        for (const key of ["coveredRequests", "excludedRecords"])
          combined[key] = previous[key] + item[key];
        combined.exclusions = { ...previous.exclusions };
        for (const [key, count] of Object.entries(item.exclusions || {}))
          combined.exclusions[key] = (combined.exclusions[key] || 0) + count;
        comparisons.set(item.id, combined);
      }
    }
  }
  const list = [...records.values()];
  const totals = Object.fromEntries(
    usageFields.map((key) => [
      key,
      list.length
        ? list.reduce((sum, row) => sumNullable(sum, row[key]), 0)
        : null,
    ]),
  );
  return {
    records: list,
    totals,
    inconsistent,
    knownActualCostUsd: list.reduce(
      (sum, row) => sum + (row.actualCostUsd ?? 0),
      0,
    ),
    unknownCostRecords: list.filter((row) => row.actualCostUsd == null).length,
    comparisons: [...comparisons.values()].map((item) => {
      const savedUsd =
        item.estimatedCostUsd == null || item.coveredActualCostUsd == null
          ? null
          : item.estimatedCostUsd - item.coveredActualCostUsd;
      return {
        ...item,
        savedUsd,
        percentSaved:
          savedUsd == null || !item.estimatedCostUsd
            ? null
            : (savedUsd / item.estimatedCostUsd) * 100,
      };
    }),
  };
}
